/**
 * PATH: apps/ai/src/pages/openclaw-concepts/RoadmapSection.tsx
 * PURPOSE: Implementation roadmap and footer
 */

import { Rocket, Brain, Code, ExternalLink } from 'lucide-react'

const phases = [
  { number: 1, title: 'Foundation', timeline: 'Weeks 1-4', color: 'emerald', items: [
    { name: 'Scheduled Reports (Cron)', status: 'ready' }, { name: 'Basic Anomaly Detection', status: 'ready' },
    { name: 'Morning Brief Email', status: 'ready' }, { name: 'Tool Confirmation System', status: 'in-progress' }, { name: 'Persistent Memory Store', status: 'planned' }] },
  { number: 2, title: 'Intelligence', timeline: 'Weeks 5-8', color: 'blue', items: [
    { name: 'Event-Driven Triggers', status: 'planned' }, { name: 'Predictive Cash Flow', status: 'planned' },
    { name: 'Smart Alert Routing', status: 'planned' }, { name: 'Context Compaction', status: 'planned' }, { name: 'Multi-Agent Coordination', status: 'exploring' }] },
  { number: 3, title: 'Autonomy', timeline: 'Weeks 9-12', color: 'purple', items: [
    { name: 'Autonomous Actions', status: 'exploring' }, { name: 'Voice Interface', status: 'exploring' },
    { name: 'Learning & Adaptation', status: 'exploring' }, { name: 'Custom Skills System', status: 'exploring' }, { name: 'ERP Integrations', status: 'future' }] },
]

const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
  ready: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Ready' },
  'in-progress': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Building' },
  planned: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Planned' },
  exploring: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Exploring' },
  future: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Future' },
}

export function RoadmapSection() {
  return (
    <div className="relative py-24 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6"><Rocket className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-400">Implementation Roadmap</span></div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">From Concept to <span className="text-emerald-400">Reality</span></h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">A phased approach to building the future of Quotech AI</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {phases.map((phase) => (
            <div key={phase.number} className="relative">
              <div className={`absolute -top-4 -left-4 w-10 h-10 rounded-xl bg-${phase.color}-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-${phase.color}-500/30 z-10`}>{phase.number}</div>
              <div className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-3xl p-6 pt-8 h-full">
                <div className="mb-4"><h3 className="text-xl font-semibold text-white">{phase.title}</h3><p className={`text-sm text-${phase.color}-400`}>{phase.timeline}</p></div>
                <div className="space-y-3">{phase.items.map((item, i) => { const style = statusStyles[item.status]; return (<div key={i} className="flex items-center justify-between"><span className="text-sm text-slate-300">{item.name}</span><span className={`px-2 py-0.5 rounded text-xs ${style.bg} ${style.text}`}>{style.label}</span></div>) })}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function Footer() {
  return (
    <div className="border-t border-slate-800 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center"><Brain className="w-5 h-5 text-white" /></div>
            <div><div className="text-white font-medium">Quotech AI Hub</div><div className="text-sm text-slate-500">Research Document - February 2026</div></div>
          </div>
          <div className="flex items-center gap-6">
            <a href="https://github.com/openclaw/openclaw" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><Code className="w-4 h-4" />OpenClaw Inspiration<ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
      </div>
    </div>
  )
}
